/**
 * Review r52 (VIS-M2, SPEC-B2): o destino da entrada. Quem abre o link da fatura (`/financeiro/<doc>?cliente=<id>`, da
 * mensagem de cobrança) sem sessão vai à `/entrar` com o destino em `state.voltar`; a entrada (código, link do e-mail,
 * senha, passkey e convite) volta a ele em vez de ir sempre ao Hoje. O link do e-mail abre noutra aba (sem o `state`), então
 * o destino também fica guardado neste aparelho por pouco tempo (`localStorage`, 30 min, lido uma vez).
 *
 * Segurança: só caminho interno do Portal. Nada de endereço externo (`https://…`, `//host`, `/\host`, `javascript:`), nem as
 * telas públicas (`/entrar`, `/convite`, `/ver-como`), nem fragmento (o `#token` dos links de entrada nunca é repetido):
 * o destino é refeito pela URL do próprio Portal e só sai se a origem continuar a mesma (sem redirecionamento aberto).
 */
const CHAVE = 'malgo.portal.destino';
const VALIDADE_MS = 30 * 60 * 1000;
const ORIGEM = 'https://portal.invalido';
const PUBLICAS = ['/entrar', '/convite', '/ver-como'];

/**
 * Caminho do próprio Portal (`/x?y`, já normalizado) ou null; serve ao destino da entrada e aos deep links do app.
 * Review r53 (SEG-B1, SPEC-B1): confere também DEPOIS de normalizar, porque ponto e ponto-ponto (`/..//host`, `/.//host`,
 * `/%2e//host`) viram `//host` na normalização; e o resultado, lido de novo, tem de dar o mesmo caminho na mesma origem.
 */
export function caminhoInterno(v: unknown): string | null {
  if (typeof v !== 'string' || !v.startsWith('/') || v.length > 500) return null;
  // `//host` e `/\host` são endereços de outro site para o navegador; caractere de controle ou barra invertida, nunca.
  const ruim = (t: string) => t.startsWith('//') || t.startsWith('/\\') || [...t].some((ch) => ch === '\\' || ch.charCodeAt(0) < 32 || ch.charCodeAt(0) === 127);
  if (ruim(v)) return null;
  let u: URL;
  try {
    u = new URL(v, ORIGEM);
  } catch {
    return null;
  }
  if (u.origin !== ORIGEM || ruim(u.pathname)) return null;
  const caminho = u.pathname + u.search;
  try {
    const de_novo = new URL(caminho, ORIGEM);
    if (de_novo.origin !== ORIGEM || de_novo.pathname + de_novo.search !== caminho) return null;
  } catch {
    return null;
  }
  return caminho;
}

/** O destino depois da entrada (`/x?y`) ou null: só caminho interno, fora as telas públicas e o Hoje sem busca. */
export function destinoInterno(v: unknown): string | null {
  const caminho = caminhoInterno(v);
  if (!caminho) return null;
  const u = new URL(caminho, ORIGEM);
  if (PUBLICAS.some((p) => u.pathname === p || u.pathname.startsWith(`${p}/`))) return null;
  if (u.pathname === '/' && !u.search) return null;
  return caminho;
}

/** Guarda o destino (o `Protegido`, ao mandar para a entrada). */
export function guardarDestino(v: unknown): void {
  const d = destinoInterno(v);
  try {
    if (d) localStorage.setItem(CHAVE, JSON.stringify({ d, em: Date.now() }));
  } catch {
    /* sem armazenamento: vale só o `state.voltar` */
  }
}

/** Para onde ir depois de entrar: o `state.voltar` (validado) ou o guardado (validado, dentro do prazo); senão o Hoje. Lê uma vez. */
export function destinoDaEntrada(state: unknown): string {
  const doState = destinoInterno((state as { voltar?: unknown } | null)?.voltar);
  let guardado: string | null = null;
  try {
    const bruto = localStorage.getItem(CHAVE);
    localStorage.removeItem(CHAVE);
    const g = bruto ? (JSON.parse(bruto) as { d?: unknown; em?: unknown }) : null;
    if (g && typeof g.em === 'number' && Date.now() - g.em >= 0 && Date.now() - g.em < VALIDADE_MS) guardado = destinoInterno(g.d);
  } catch {
    guardado = null;
  }
  return doState ?? guardado ?? '/';
}

/** O cliente do link da fatura (`?cliente=<uuid>`), ou null. */
export function clienteDoLink(search: string): string | null {
  const c = new URLSearchParams(search).get('cliente');
  return c && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c) ? c.toLowerCase() : null;
}

/** A mesma busca sem o `cliente` (depois da troca, o endereço fica limpo). */
export function semCliente(search: string): string {
  const p = new URLSearchParams(search);
  p.delete('cliente');
  const s = p.toString();
  return s ? `?${s}` : '';
}
